import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Logo } from "@/components/brand/logo";
import { BrandButton } from "@/components/brand/brand-button";

const errorMessages: Record<string, { title: string; description: string }> = {
	unable_to_create_user: {
		title: "Account already exists",
		description:
			"An account with this email already exists. Try signing in with your email and password, or use a different authentication method.",
	},
	email_not_verified: {
		title: "Email not verified",
		description:
			"Please verify your email address before signing in. Check your inbox for the verification link.",
	},
	invalid_credentials: {
		title: "Invalid credentials",
		description:
			"The email or password you entered is incorrect. Please try again.",
	},
	session_expired: {
		title: "Session expired",
		description: "Your session has expired. Please sign in again to continue.",
	},
	access_denied: {
		title: "Access denied",
		description: "You don't have permission to access this resource.",
	},
	default: {
		title: "Authentication error",
		description:
			"Something went wrong during authentication. Please try again.",
	},
};

export const Route = createFileRoute("/api/auth/error")({
	component: AuthErrorPage,
	validateSearch: (search: Record<string, unknown>) => ({
		error: (search.error as string) || "default",
	}),
});

function AuthErrorPage() {
	const { error } = useSearch({ from: "/api/auth/error" });
	const errorInfo = errorMessages[error] || errorMessages.default;

	return (
		<div
			className="min-h-svh flex flex-col"
			style={{ backgroundColor: "var(--km-surface)" }}
		>
			<div className="px-6 py-4 lg:px-12">
				<Logo size="sm" />
			</div>

			<div className="flex-1 flex items-center justify-center px-6 py-12">
				<div className="w-full max-w-md">
					<div
						className="p-8 overflow-hidden"
						style={{ backgroundColor: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						<div className="flex justify-center mb-6">
							<div
								className="w-16 h-16 flex items-center justify-center"
								style={{ backgroundColor: "rgba(255,110,132,0.1)" }}
							>
								<span
									className="material-symbols-outlined"
									aria-hidden="true"
									style={{ fontSize: "32px", color: "#ff6e84", fontVariationSettings: "'FILL' 1" }}
								>
									error
								</span>
							</div>
						</div>

						<div className="text-center mb-8">
							<p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Authentication Error</p>
							<h1
								className="text-2xl font-semibold mb-2"
								style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								{errorInfo.title}
							</h1>
							<p style={{ color: "var(--km-on-surface-muted)" }}>{errorInfo.description}</p>
						</div>

						{error !== "default" && (
							<div className="mb-6 px-4 py-2.5 text-center" style={{ backgroundColor: "rgba(234,40,4,0.05)", border: "1px solid rgba(234,40,4,0.15)", borderLeft: "2px solid #6B3FF2" }}>
								<p className="text-xs" style={{ color: "#6B3FF2", fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}>
									ERR_{error.toUpperCase()}
								</p>
							</div>
						)}

						<div className="space-y-3">
							<Link to="/login" className="block">
								<BrandButton className="w-full">Sign in</BrandButton>
							</Link>
							<Link to="/signup" className="block">
								<BrandButton variant="secondary" className="w-full">
									Create account
								</BrandButton>
							</Link>
						</div>

						<div className="mt-6 text-center">
							<Link
								to="/"
								className="inline-flex items-center gap-2 text-sm transition-colors"
								style={{ color: "var(--km-on-surface-muted)" }}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
								Back to home
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
